import { Line } from '@react-three/drei'
import type { ThreeEvent } from '@react-three/fiber'
import { useMemo, useRef } from 'react'
import { multiplicar, paraArray, somar, subtrair, vetor, type Vetor3 } from '../../dominio/geometria/vetor3'
import type { EixoColuna } from '../../dominio/geometria/anguloExtracao'
import { ancoraPorId, type AncoraOcupante } from '../../dominio/didatico/perfilVeiculo'
import { alturaPelveEmPe, perfilVitimaPorId, type ProporcoesVitima } from '../../dominio/didatico/perfisVitima'
import {
  aberturaMaisAlinhada,
  amostrarRota,
  leituraDaRota,
  PA_DE_FORNO,
  type RotaExtracao,
} from '../../dominio/didatico/rotasExtracao'
import { aberturasDeSaida } from '../../dominio/didatico/aberturas'
import { SetaDirecionalExtracao3D } from './SetaDirecionalExtracao3D'
import {
  postoDoVeiculo,
  direcaoElevacaoPaDeForno,
  vitimaDeitada,
  yDoPlanoDeApoio,
  type ConfiguracaoVeiculo,
  colocacaoNaConfiguracao,
} from '../../dominio/didatico/configuracaoVeiculo'
import { eixoColunaDaVitima, type ColocacaoVitima } from '../../dominio/didatico/rigVitima'
import { alinharPranchaNaPaDeForno, alinharPranchaSobreRodas } from '../../dominio/didatico/alinhamentoPrancha'
import { CORES_FAIXA_GIRO, CORES_SEMANTICAS } from '../../dominio/didatico/tokens'
import type { OrientacaoSeta } from '../../dominio/didatico/setaCervical'
import {
  airbagsDaPosicao,
  vitimaEstaCoberta,
  type IdProcedimento,
} from '../../dominio/didatico/procedimentosInternos'
import { Rotulo3D } from '../componentes/Rotulo3D'
import { Vetor3D } from '../componentes/Vetor3D'
import { useMaquete } from '../componentes/contextoMaquete'
import { VeiculoDidatico } from '../veiculo/VeiculoDidatico'
import { OcupanteSelecionavel } from '../componentes/OcupanteSelecionavel'
import { VitimaProcedural } from '../vitima/VitimaProcedural'
import { SilhuetaBancoAnterior, type EstadoBancoAnterior } from './SilhuetaBancoAnterior'
import { ArcoAngulo } from './ArcoAngulo'
import { MeiaPrancha3D } from './MeiaPrancha3D'
import { Prancha } from './Prancha'
import { SetaCervical } from './SetaCervical'
import { TrajetoriaExtracao } from './TrajetoriaExtracao'
import type { EstadoClinico } from '../../dominio/didatico/ocupantes'
import {
  portaObstruidaPorObstaculo,
  type ObstaculoExterno,
  type TipoObstaculoExterno,
} from '../../dominio/didatico/obstaculosExternos'
import type { AprisionamentoCenario } from '../../dominio/didatico/cenariosExtracao'
import { MarcadoresAprisionamento } from './MarcadoresAprisionamento'
import { DestaqueAchados3D, type AchadoDestacavel } from './DestaqueAchados3D'

export type FerramentaExtracao = 'nenhuma' | 'trajeto' | TipoObstaculoExterno

export interface VitimaSecundariaExtracao {
  id: string
  nome: string
  estadoClinico: EstadoClinico
  /** false enquanto o aluno não avaliou esta vítima o suficiente — ver vitimaSeveridadeVisivel */
  estadoClinicoVisivel: boolean
  proporcoes: ProporcoesVitima
  colocacao: ColocacaoVitima
  interfere: boolean
}

export interface PropsCenaExtracao {
  eixoColuna: EixoColuna
  ancora: AncoraOcupante
  proporcoes: ProporcoesVitima
  /** em que posição o veículo parou */
  configuracao: ConfiguracaoVeiculo
  /** colocação inicial da vítima, já resolvida pela configuração */
  colocacaoInicial: ColocacaoVitima
  rota: RotaExtracao
  rotaComparada: RotaExtracao | null
  tempo: number
  comPrancha: boolean
  comMeiaPrancha?: boolean
  colarCervical?: boolean
  /** o corpo é girado em bloco: sem torção residual na cervical */
  girarEmBloco: boolean
  mostrarEixoColuna: boolean
  mostrarTrajetoria: boolean
  mostrarArco: boolean
  mostrarPosicaoInicial: boolean
  mostrarSetaCervical: boolean
  orientacaoSeta: OrientacaoSeta
  aoAlterarOrientacaoSeta: (orientacao: OrientacaoSeta) => void
  /** procedimentos internos já feitos (P3.3): decide se airbag/vítima aparecem protegidos */
  procedimentosFeitos: readonly IdProcedimento[]
  /** Fase 4: posição anterior do banco da âncora em foco, se o aluno pediu para ver */
  silhuetaBancoAnterior?: EstadoBancoAnterior | null
  vitimasSecundarias: readonly VitimaSecundariaExtracao[]
  /** id de quem está sendo retirado, para o corpo ser clicável na cena */
  idVitimaEmRetirada?: string
  /** troca o foco ao tocar numa vítima, em vez de escolhê-la na lista */
  aoSelecionarVitima?: (id: string) => void
  obstaculosExternos: readonly ObstaculoExterno[]
  ferramenta?: FerramentaExtracao
  aoIniciarTrajeto?: (ponto: Vetor3) => void
  aoAdicionarPontoTrajeto?: (ponto: Vetor3) => void
  direcaoSeta?: Vetor3
  aoAlterarDirecaoSeta?: (direcao: Vetor3) => void
  aoPosicionarObstaculo: (tipo: TipoObstaculoExterno, x: number, z: number) => void
  aoRemoverObstaculo: (id: string) => void
  aprisionamentos?: readonly AprisionamentoCenario[]
  /** achados do XABCDE já revelados desta vítima, com região pra destacar (Fase 5) */
  achadosDestacados?: readonly AchadoDestacavel[]
  /** Outros veículos do cenário. A interface clínica continua única; tocar no corpo troca o foco. */
  veiculosSecundarios?: readonly {
    id: string
    nome: string
    configuracao: ConfiguracaoVeiculo
    decubito: 'dorsal' | 'ventral'
    sentidoCabeca: 'frente' | 'traseira'
    vitimas: readonly {
      id: string
      nome: string
      idAncora: string
      idPerfil: string
      estadoClinico: EstadoClinico
    }[]
  }[]
}

const COR_COMPARACAO = '#ffc44d'

/**
 * Cena de extração.
 *
 * Ângulo, arco, trajetória e rotação do corpo derivam todos do eixo da coluna
 * da vítima na âncora. A rota em si pode ser uma demonstração nominal ou uma
 * saída real por uma abertura do veículo — em ambos os casos o número exibido
 * sai de `calcularAnguloExtracao`, nunca de um texto fixo.
 */
export function CenaExtracao(props: PropsCenaExtracao) {
  const { perfil } = useMaquete()
  const { eixoColuna, ancora, proporcoes, rota, colocacaoInicial } = props

  const deitada = vitimaDeitada(props.configuracao)
  const tombado = props.configuracao === 'lateral'
  const paDeForno = props.configuracao !== 'sobreRodas'
  const usarPrancha = props.comPrancha || paDeForno
  const direcaoElevacao = direcaoElevacaoPaDeForno(props.configuracao)
  const alturaPaDeForno = 0.24
  // Sobre as rodas, sem prancha, a vítima termina de pé. No capotado e no
  // tombado ela já começa deitada e segue a sequência da pá de forno.
  const elevacaoFinal =
    deitada || usarPrancha ? 0 : alturaPelveEmPe(proporcoes) - ancora.posicao.y
  // girar em bloco zera a torção residual: pelve, tronco e cabeça giram juntos
  const opcoes = {
    elevacaoFinal,
    comPrancha: usarPrancha,
    deitada,
    ...(paDeForno ? { paDeForno: { direcaoElevacao, altura: alturaPaDeForno } } : {}),
    ...(props.girarEmBloco ? { fracaoTorcao: 0 } : {}),
  }

  const amostra = amostrarRota(rota, eixoColuna, props.tempo, opcoes)
  const amostraComparada = props.rotaComparada
    ? amostrarRota(props.rotaComparada, eixoColuna, props.tempo, opcoes)
    : null

  /**
   * O giro e a reclinação da rota SOMAM-SE à colocação inicial em vez de
   * substituí-la. Sentada a colocação é neutra e o resultado é o de sempre;
   * deitada no teto, é a soma que preserva decúbito e sentido da cabeça enquanto
   * o corpo é alinhado à saída.
   */
  const comMovimento = (a: typeof amostra): ColocacaoVitima => ({
    posicaoPelve: somar(colocacaoInicial.posicaoPelve, a.deslocamento),
    guinadaRad: colocacaoInicial.guinadaRad + a.guinadaRad,
    reclinacaoRad: (colocacaoInicial.reclinacaoRad ?? 0) + a.reclinacaoRad,
    rolagemRad: colocacaoInicial.rolagemRad ?? 0,
  })

  const colocacaoAtual = comMovimento(amostra)
  // Na pá de forno a vítima fica alinhada; eventual mudança de direção é da prancha.
  const colocacaoDaPrancha = paDeForno
    ? { ...colocacaoAtual, guinadaRad: colocacaoInicial.guinadaRad + amostra.guinadaPranchaRad }
    : colocacaoAtual
  /* A prancha acompanha a guinada e o deslocamento do corpo, mas não sobe
     junto durante a elevação: ela precisa permanecer no plano para entrar por
     baixo. Recalcular a base da coluna com essa colocação mantém o eixo longo
     da prancha exatamente sob o eixo corporal, inclusive após o alinhamento. */
  const alinhamentoPrancha = paDeForno
    ? alinharPranchaNaPaDeForno(
        colocacaoDaPrancha,
        proporcoes,
        amostra.pose,
        direcaoElevacao,
        alturaPaDeForno,
        amostra.fracaoLevantar,
      )
    : {
        ...alinharPranchaSobreRodas(
          colocacaoAtual,
          proporcoes,
          amostra.pose,
          subtrair(rota.pontoSaida, eixoColuna.baseColuna),
        ),
      }
  // a cor do arco diz o que o número sozinho não dizia: 90° custa mais que 30°.
  // A rota comparada fica no roxo padrão, para as duas não competirem
  const corDoGiro = CORES_FAIXA_GIRO[leituraDaRota(rota).faixa]
  const yApoio = yDoPlanoDeApoio(perfil, props.configuracao)
  // arco e trajetória vivem no plano em que o corpo se apoia
  const noPlanoDeApoio = (p: { x: number; z: number }) => vetor(p.x, yApoio, p.z)
  const posto = postoDoVeiculo(perfil, props.configuracao)
  const aberturaAlinhada = useMemo(() => {
    if (rota.origem !== 'livre') return null
    const dir = props.direcaoSeta ?? subtrair(rota.pontoSaida, eixoColuna.baseColuna)
    return aberturaMaisAlinhada(eixoColuna, dir, aberturasDeSaida(perfil))
  }, [rota, props.direcaoSeta, eixoColuna, perfil])

  // As portas devem ser abertas antes da retirada para garantir acesso e espaço,
  // exceto se houver obstáculo externo bloqueando a porta ou se estiver contra o solo.
  // No veículo tombado e no capotado, a retirada é feita exclusivamente pelo porta-malas: não abre as portas.
  const aberturasPortas = useMemo(() => {
    const portas: Array<'portaDianteiraEsquerda' | 'portaDianteiraDireita' | 'portaTraseiraEsquerda' | 'portaTraseiraDireita'> = [
      'portaDianteiraEsquerda',
      'portaDianteiraDireita',
      'portaTraseiraEsquerda',
      'portaTraseiraDireita',
    ]

    const resultado: Partial<Record<'portaDianteiraEsquerda' | 'portaDianteiraDireita' | 'portaTraseiraEsquerda' | 'portaTraseiraDireita', number>> = {}

    // No tombado não abre as portas, apenas o porta-malas. No capotado as portas abrem na sequência:
    if (props.configuracao === 'lateral') {
      for (const idPorta of portas) {
        resultado[idPorta] = 0
      }
      return resultado
    }

    const fracaoAberturaPorta = amostra.fracaoAberturaPortas ?? 1

    for (const idPorta of portas) {
      if (portaObstruidaPorObstaculo(idPorta, perfil, props.obstaculosExternos)) {
        resultado[idPorta] = 0
        continue
      }
      resultado[idPorta] = fracaoAberturaPorta
    }

    return resultado
  }, [perfil, props.configuracao, props.obstaculosExternos, amostra.fracaoAberturaPortas])

  const tampaTraseiraObstruida = useMemo(
    () => portaObstruidaPorObstaculo('tampaTraseira', perfil, props.obstaculosExternos),
    [perfil, props.obstaculosExternos],
  )
  const abrirTampaTraseira = !tampaTraseiraObstruida && (
    props.configuracao === 'lateral' ||
    props.configuracao === 'invertido' ||
    rota.anguloNominal === 0 ||
    rota.abertura?.id === 'tampaTraseira'
  )

  return (
    <>
    <group position={[0, posto.alturaApoio, 0]} rotation={[posto.inclinacaoLongitudinalRad, 0, posto.rolagemRad]}>
      <VeiculoDidatico
        perfil={perfil}
        aberturaPortas={aberturasPortas}
        aberturaTampaTraseira={abrirTampaTraseira ? (amostra.fracaoAberturaTampa ?? 1) : 0}
      />

      {props.silhuetaBancoAnterior && (
        <SilhuetaBancoAnterior perfil={perfil} ancora={ancora} estado={props.silhuetaBancoAnterior} />
      )}

      {props.vitimasSecundarias.map((vitima) => (
        <OcupanteSelecionavel key={vitima.id} id={vitima.id} aoSelecionar={props.aoSelecionarVitima}>
          <VitimaProcedural
            proporcoes={vitima.proporcoes}
            colocacao={vitima.colocacao}
            fantasma={!vitima.interfere}
          />
          <Rotulo3D
            posicao={somar(vitima.colocacao.posicaoPelve, vetor(0, vitima.proporcoes.alturaTronco + 0.55, 0))}
            texto={
              vitima.estadoClinicoVisivel
                ? `${vitima.estadoClinico === 'grave' ? 'GRAVE' : 'ESTÁVEL'} · ${vitima.interfere ? 'na rota' : vitima.nome}`
                : vitima.interfere ? 'na rota' : vitima.nome
            }
            variante={vitima.interfere ? 'risco' : 'neutro'}
            fator={4.5}
          />
        </OcupanteSelecionavel>
      ))}

      {usarPrancha && (
        <Prancha
          base={alinhamentoPrancha.base}
          pontoSaida={rota.pontoSaida}
          altura={
            props.configuracao === 'lateral'
              ? colocacaoAtual.posicaoPelve.y
              : deitada
                ? eixoColuna.baseColuna.y - 0.03
                : ancora.alturaAssento + 0.03
          }
          fracaoEntrada={amostra.fracaoPrancha}
          deslocamento={vetor(0, 0, 0)}
          configuracao={props.configuracao}
          direcaoCorpo={alinhamentoPrancha.direcaoCorpo}
        />
      )}

      {/* posição inicial como fantasma: dá referência de quanto o corpo já saiu */}
      {props.mostrarPosicaoInicial && props.tempo > 0.03 && (
        <VitimaProcedural
          proporcoes={proporcoes}
          colocacao={colocacaoInicial}
          pose={deitada ? amostra.pose : undefined}
          fantasma
        />
      )}

      <OcupanteSelecionavel
        id={props.idVitimaEmRetirada ?? 'vitima-em-retirada'}
        aoSelecionar={props.aoSelecionarVitima}
      >
        <VitimaProcedural
          proporcoes={proporcoes}
          colocacao={colocacaoAtual}
          pose={amostra.pose}
          mostrarEixoColuna={props.mostrarEixoColuna}
          colarCervical={
            props.colarCervical ||
            props.procedimentosFeitos.includes('colocarColarCervical') ||
            (usarPrancha && (amostra.fracaoColarCervical ?? 0) >= 0.5)
          }
        />
      </OcupanteSelecionavel>
      <MarcadoresAprisionamento
        colocacao={colocacaoAtual}
        proporcoes={proporcoes}
        aprisionamentos={props.aprisionamentos ?? []}
      />
      <DestaqueAchados3D
        colocacao={colocacaoAtual}
        proporcoes={proporcoes}
        pose={amostra.pose}
        achados={props.achadosDestacados ?? []}
      />

      {/* Meia-prancha dorsal posicionada nas costas da vítima */}
      {props.comMeiaPrancha && (
        <MeiaPrancha3D
          posicao={[
            colocacaoAtual.posicaoPelve.x - Math.sin(colocacaoAtual.guinadaRad) * 0.12,
            colocacaoAtual.posicaoPelve.y + 0.26,
            colocacaoAtual.posicaoPelve.z - Math.cos(colocacaoAtual.guinadaRad) * 0.12,
          ]}
          rotacao={[-(colocacaoAtual.reclinacaoRad ?? 0), colocacaoAtual.guinadaRad, 0]}
        />
      )}

      {/* eixo da coluna prolongado, para o aluno ver o eixo anatômico de longe */}
      {props.mostrarEixoColuna && (
        <Line
          points={[
            paraArray(noPlanoDeApoio(eixoColuna.baseColuna)),
            paraArray(eixoColuna.topoColuna),
          ]}
          color={CORES_SEMANTICAS.eixoAnatomico}
          lineWidth={2}
          dashed
          dashSize={0.09}
          gapSize={0.07}
        />
      )}

      {props.mostrarSetaCervical && (
        <SetaCervical
          eixoColuna={eixoColuna}
          orientacao={props.orientacaoSeta}
          aoAlterar={props.aoAlterarOrientacaoSeta}
        />
      )}

      {props.mostrarTrajetoria && (
        <TrajetoriaExtracao
          rota={rota}
          eixoColuna={eixoColuna}
          opcoes={opcoes}
          rotulo={props.rotaComparada ? rotuloDaRota(rota) : undefined}
          mostrarPontoSaida={rota.origem !== 'livre'}
        />
      )}
      {rota.origem === 'livre' && props.aoAlterarDirecaoSeta && (
        <SetaDirecionalExtracao3D
          centro={eixoColuna.baseColuna}
          direcao={props.direcaoSeta ?? subtrair(rota.pontoSaida, eixoColuna.baseColuna)}
          anguloGraus={rota.resultado.anguloGraus ?? 0}
          faixaGiro={leituraDaRota(rota).faixa}
          aberturaProximaNome={aberturaAlinhada?.curto ?? null}
          aoAlterarDirecao={props.aoAlterarDirecaoSeta}
          comprimento={2.4}
        />
      )}
      {props.mostrarArco && (
        <>
          <ArcoAngulo
            rota={rota}
            centro={eixoColuna.baseColuna}
            mostrarValor={false}
            cor={corDoGiro}
          />
          {/* o mesmo arco projetado no plano de apoio: é nele que o aluno compara
              os ângulos sem sofrer a perspectiva do corpo */}
          <ArcoAngulo
            rota={rota}
            centro={noPlanoDeApoio(eixoColuna.baseColuna)}
            raio={1.25}
            espessura={6}
            cor={corDoGiro}
          />
        </>
      )}

      {/* rota comparada: mesmo veículo, mesma vítima, mesmo instante */}
      {props.rotaComparada && amostraComparada && (
        <group>
          <TrajetoriaExtracao
            rota={props.rotaComparada}
            eixoColuna={eixoColuna}
            opcoes={opcoes}
            cor={COR_COMPARACAO}
            rotulo={rotuloDaRota(props.rotaComparada)}
          />
          {props.mostrarArco && (
            <ArcoAngulo
              rota={props.rotaComparada}
              centro={noPlanoDeApoio(eixoColuna.baseColuna)}
              raio={1.65}
              espessura={5}
            />
          )}
          <VitimaProcedural
            proporcoes={proporcoes}
            colocacao={comMovimento(amostraComparada)}
            pose={amostraComparada.pose}
            fantasma
          />
        </group>
      )}

      {vitimaEstaCoberta(props.procedimentosFeitos) && (
        <mesh position={paraArray(somar(colocacaoAtual.posicaoPelve, vetor(0, proporcoes.alturaPelve, 0)))}>
          <boxGeometry args={[proporcoes.larguraOmbros + 0.06, 0.04, proporcoes.alturaTronco + 0.3]} />
          <meshStandardMaterial color="#dce6ee" roughness={0.85} transparent opacity={0.88} />
        </mesh>
      )}

      {props.procedimentosFeitos.includes('protegerAirbag') && airbagsDaPosicao(perfil, ancora.id).length > 0 && (
        <CapaDeProtecao ancora={ancora} perfil={perfil} />
      )}

      <Rotulo3D
        posicao={somar(eixoColuna.topoColuna, vetor(0, deitada ? 0.35 : 0.6, 0))}
        texto={amostra.fase}
        variante="neutro"
        fator={5}
        sempreVisivel
      />

      {paDeForno && props.tempo >= PA_DE_FORNO.elevar[0] && props.tempo <= PA_DE_FORNO.abaixar[1] && (
        <Vetor3D
          origem={somar(colocacaoInicial.posicaoPelve, vetor(0, 0.18, 0))}
          destino={somar(colocacaoInicial.posicaoPelve, somar(vetor(0, 0.18, 0), multiplicar(direcaoElevacao, 0.62)))}
          cor={CORES_SEMANTICAS.trajetoria}
          espessura={6}
        />
      )}
    </group>

    {props.obstaculosExternos.map((obstaculo) => (
      <ObstaculoExternoNaCena
        key={obstaculo.id}
        obstaculo={obstaculo}
        perfil={perfil}
        aoRemover={() => props.aoRemoverObstaculo(obstaculo.id)}
      />
    ))}
    {props.veiculosSecundarios?.map((veiculo, indice) => {
      const postoSecundario = postoDoVeiculo(perfil, veiculo.configuracao)
      return (
        <group
          key={veiculo.id}
          position={[(indice + 1) * 4.8, 0, 0]}
          onClick={(evento) => {
            evento.stopPropagation()
            const primeira = veiculo.vitimas[0]
            if (primeira) props.aoSelecionarVitima?.(primeira.id)
          }}
        >
          <group position={[0, postoSecundario.alturaApoio, 0]} rotation={[postoSecundario.inclinacaoLongitudinalRad, 0, postoSecundario.rolagemRad]}>
            <VeiculoDidatico perfil={perfil} />
            {veiculo.vitimas.map((item) => {
              const ancoraVitima = ancoraPorId(perfil, item.idAncora)
              const proporcoesVitima = perfilVitimaPorId(item.idPerfil)
              const colocacao = colocacaoNaConfiguracao(
                perfil,
                ancoraVitima,
                proporcoesVitima,
                veiculo.configuracao,
                veiculo.decubito,
                veiculo.sentidoCabeca,
              )
              return (
                <OcupanteSelecionavel key={item.id} id={item.id} aoSelecionar={props.aoSelecionarVitima}>
                  <VitimaProcedural proporcoes={proporcoesVitima} colocacao={colocacao} />
                  <Rotulo3D
                    posicao={somar(colocacao.posicaoPelve, vetor(0, proporcoesVitima.alturaTronco + 0.5, 0))}
                    texto={`${veiculo.nome} · ${item.nome}`}
                    variante="neutro"
                    fator={4.5}
                  />
                </OcupanteSelecionavel>
              )
            })}
          </group>
        </group>
      )
    })}
    {props.ferramenta !== 'nenhuma' && props.ferramenta !== 'trajeto' && (
      <mesh
        position={[0, 0.014, 0]}
        rotation={[-Math.PI / 2, 0, 0]}
        onClick={(evento) => {
          evento.stopPropagation()
          props.aoPosicionarObstaculo(props.ferramenta as TipoObstaculoExterno, Number(evento.point.x.toFixed(2)), Number(evento.point.z.toFixed(2)))
        }}
      >
        <planeGeometry args={[16, 16]} />
        <meshBasicMaterial transparent opacity={0.035} color="#f0c24b" depthWrite={false} />
      </mesh>
    )}
    </>
  )
}

function ObstaculoExternoNaCena({ obstaculo, perfil, aoRemover }: { obstaculo: ObstaculoExterno; perfil: ReturnType<typeof useMaquete>['perfil']; aoRemover: () => void }) {
  const remover = (evento: ThreeEvent<MouseEvent>) => {
    evento.stopPropagation()
    aoRemover()
  }
  if (obstaculo.tipo === 'poste') {
    return <mesh position={[obstaculo.x, 1.25, obstaculo.z]} onClick={remover}><cylinderGeometry args={[0.14, 0.18, 2.5, 14]} /><meshStandardMaterial color="#79838b" roughness={0.9} /></mesh>
  }
  if (obstaculo.tipo === 'muro') {
    return <mesh position={[obstaculo.x, 0.8, obstaculo.z]} onClick={remover}><boxGeometry args={[0.28, 1.6, 3.2]} /><meshStandardMaterial color="#7b7168" roughness={1} /></mesh>
  }
  return (
    <group position={[obstaculo.x, 0, obstaculo.z]} onClick={remover}>
      <mesh position={[0, perfil.cinturaY * 0.45, 0]}><boxGeometry args={[perfil.largura, perfil.cinturaY * 0.9, perfil.comprimento * 0.82]} /><meshStandardMaterial color="#9b4b45" roughness={0.72} /></mesh>
      <mesh position={[0, perfil.cinturaY, -0.12]}><boxGeometry args={[perfil.largura * 0.8, perfil.tetoY * 0.42, perfil.comprimento * 0.42]} /><meshStandardMaterial color="#243b4b" roughness={0.45} /></mesh>
    </group>
  )
}

/** Capa simbólica sobre o volante/painel, indicando o airbag protegido. */
function CapaDeProtecao({ ancora, perfil }: { ancora: AncoraOcupante; perfil: ReturnType<typeof useMaquete>['perfil'] }) {
  const y = perfil.cinturaY - 0.14
  const z = ancora.id === 'motorista' ? perfil.colunaA.zBase - 0.34 : perfil.colunaA.zBase - 0.16
  return (
    <mesh position={[ancora.posicao.x, y, z]}>
      <boxGeometry args={[0.3, 0.24, 0.06]} />
      <meshStandardMaterial color="#e8d3a6" roughness={0.8} transparent opacity={0.85} />
    </mesh>
  )
}

/** Rótulo da rota com o número que de fato vale para a postura da vítima. */
function rotuloDaRota(rota: RotaExtracao): string {
  const leitura = leituraDaRota(rota)
  return `${leitura.convencao}° · ${rota.nome}`
}
